/**
 * Evento de telemetría de un intento de captura y carga.
 *
 * **El destino ya no es Sentry.** El evento viaja al Gateway (`POST /api/telemetry`) y de ahí al
 * agregador de logs. Sentry queda solo para errores y excepciones, que es lo que el autor definió al
 * reescribir la historia: la telemetría incluye los éxitos, y cobrarlos contra la cuota de una
 * herramienta de errores no tiene sentido.
 *
 * El evento se arma desde una **lista blanca** y nunca esparciendo el contexto de entrada. Eso no es
 * estilo: dentro de `addPhotoAndDeclare` hay latitud y longitud en alcance (`getDeviceLocation`), y
 * un `...ctx` las arrastraría el día que alguien las agregue «porque estaban disponibles».
 * `findSensitiveKeys` es la guarda ejecutable de eso, no una convención.
 *
 * **Lo que este módulo NO manda, y es deliberado:** modelo, marca, sistema operativo, plataforma,
 * versión de app y tipo de conexión. El servidor los observa de las cabeceras que ya envía
 * `ApiService.getHeaders` y los escribe él (`Controller/Telemetry.ts`, `request.metadata`).
 * Mandarlos también en el cuerpo sería tener el dato dos veces y descubrir algún día que difieren.
 */

/** Vocabulario del cliente en los puntos de llamada. Se traduce al del servidor al armar. */
export type CaptureMethod = 'camera' | 'file';
export type UploadOutcome = 'success' | 'client_rejection' | 'upload_failure';

/**
 * Literales del contrato del servidor (`Core/Validation/Telemetry.ts` del gateway), duplicados acá
 * porque el cliente tiene que producirlos. Dos literales en repos distintos derivan solos: los
 * compara `npm run verify:upload-telemetry` leyendo el archivo del gateway, y **falla si no lo
 * encuentra** en vez de saltearse.
 */
export const TELEMETRY_RESULTS = ['exito', 'rechazo_cliente', 'rechazo_s3', 'fallo'] as const;
export const TELEMETRY_METHODS = ['getUserMedia', 'input_file'] as const;
export const TELEMETRY_CAUSES = [
  'cero_bytes',
  'imagen_negra',
  'limite_peso',
  'error_s3',
  'formato_no_admitido',
  'otro',
] as const;
export const TELEMETRY_NETWORKS = ['wifi', 'cellular', 'none', 'unknown'] as const;
export const TELEMETRY_EXTS = ['jpg', 'jpeg', 'png', 'mp4', 'pdf'] as const;
/**
 * De qué camino de captura viene el intento. **Decide qué identificadores son exigibles**: una foto
 * de formulario se sube a S3 pero no se declara, así que tiene `key` y no tiene `uuid`. El servidor
 * lee la ausencia del campo como `inspeccion` —el caso estricto— para que una app instalada de una
 * versión anterior no pueda colar un éxito sin uuid por no conocerlo.
 */
export const TELEMETRY_ORIGINS = ['inspeccion', 'formulario'] as const;

export type TelemetryResult = (typeof TELEMETRY_RESULTS)[number];
export type TelemetryMethod = (typeof TELEMETRY_METHODS)[number];
export type TelemetryCause = (typeof TELEMETRY_CAUSES)[number];
export type TelemetryNetwork = (typeof TELEMETRY_NETWORKS)[number];
export type TelemetryExt = (typeof TELEMETRY_EXTS)[number];
export type TelemetryOrigin = (typeof TELEMETRY_ORIGINS)[number];

/** Tope de eventos por lote del servidor. Mismo duplicado, misma guarda cruzada. */
export const MAX_EVENTS_PER_BATCH = 100;

/** Evento tal como viaja por el cable. Espeja el esquema del servidor y nada más. */
export interface TelemetryEvent {
  /** Nombra el intento aunque no haya objeto en S3. Obligatorio siempre; es lo único que lo hace. */
  attempt_id: string;
  inspection_id: number;
  result: TelemetryResult;
  capture_method: TelemetryMethod;
  /**
   * Dos llaves de cruce distintas, y por eso se mandan distinto. `key` nombra el objeto en S3 y
   * existe en cuanto se pidió la URL prefirmada; `uuid` nombra el registro **declarado** y solo
   * existe en el camino de inspección, porque una foto de formulario no se declara.
   *
   * Los dos faltan cuando el intento murió antes de la prefirmada —un rechazo de cliente, o un
   * fallo temprano—. El servidor exige `key` solo en el éxito y `uuid` solo en un éxito que no sea
   * de formulario: exigirlos antes descartaba justo los eventos de fallo que hay que clasificar.
   */
  uuid?: string;
  key?: string;
  origin?: TelemetryOrigin;
  rejection_cause?: TelemetryCause;
  ext?: TelemetryExt;
  size_metadata_bytes?: number;
  size_real_bytes?: number;
  uploaded_size_bytes?: number;
  /** Fracción de píxeles oscuros, 0 a 1. NO es brillo medio: 0,95 es una imagen muy oscura. */
  dark_pixel_ratio?: number;
  prepare_ms?: number;
  upload_ms?: number;
  total_ms?: number;
  network?: TelemetryNetwork;
  occurred_at?: string;
}

export interface UploadAttemptContext {
  attemptId: string;
  photoId: number;
  captureMethod?: CaptureMethod;
  ext?: string;
  outcome: UploadOutcome;
  /** El `name` de la clase de `PhotoRejectedError`. Se traduce a la lista cerrada del servidor. */
  rejectionCause?: string;
  declaredSizeBytes?: number;
  realSizeBytes?: number;
  uploadedSizeBytes?: number;
  darkPixelRatio?: number;
  prepareMs?: number;
  uploadMs?: number;
  totalMs?: number;
  inspectionId?: number;
  fileUuid?: string;
  keyPath?: string;
  origin?: TelemetryOrigin;
  network?: string;
  occurredAt?: string;
}

/** Un valor bajo cualquiera de estas claves no puede salir del dispositivo. */
const SENSITIVE_KEY =
  /lat|lon|lng|coord|gps|geo|address|direccion|rut|mail|phone|telefono|whatsapp|token|jwt|hash|oi_encriptada/i;

/**
 * Recorre en profundidad y devuelve las rutas cuyas claves son sensibles. Vacío significa limpio.
 * Se usa como aserción del arnés y como guarda en el armado.
 */
export function findSensitiveKeys(value: unknown, prefix = '', depth = 0): string[] {
  if (value === null || typeof value !== 'object' || depth > 8) {
    return [];
  }
  const found: string[] = [];
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (SENSITIVE_KEY.test(key)) {
      found.push(path);
    }
    found.push(...findSensitiveKeys(child, path, depth + 1));
  }
  return found;
}

/**
 * Un rechazo de policy lo emite S3 al subir, no el dispositivo: va como `rechazo_s3` y no como
 * `rechazo_cliente` aunque su clase herede de `PhotoRejectedError`. Es la distinción entre «el
 * archivo no servía» y «el servidor lo rechazó», que son dos causas raíz distintas para 426.
 */
export function toResult(outcome: UploadOutcome, rejectionCause?: string): TelemetryResult {
  if (outcome === 'success') {
    return 'exito';
  }
  if (outcome === 'upload_failure') {
    return 'fallo';
  }
  return rejectionCause === 'UploadPolicyRejectedError' ? 'rechazo_s3' : 'rechazo_cliente';
}

/**
 * `UndecodableFileError` no tiene casilla exacta en la lista cerrada: significa que el decodificador
 * no pudo leer el archivo, que en la práctica es un archivo corrupto o un formato que el navegador
 * no abre. Va a `formato_no_admitido` por ser lo más informativo disponible — `otro` sería exacto y
 * no diría nada. Anotado como pendiente con el dueño del esquema.
 */
const CAUSE_BY_ERROR: Record<string, TelemetryCause> = {
  EmptyFileError: 'cero_bytes',
  BlackImageError: 'imagen_negra',
  FileSizeLimitError: 'limite_peso',
  UploadPolicyRejectedError: 'error_s3',
  UndecodableFileError: 'formato_no_admitido',
};

export function toCause(rejectionCause?: string): TelemetryCause | undefined {
  if (!rejectionCause) {
    return undefined;
  }
  return CAUSE_BY_ERROR[rejectionCause] ?? 'otro';
}

/**
 * El default es `input_file` y no `getUserMedia`: una foto sin método declarado viene de un reintento
 * encolado por una versión anterior, y suponer cámara inflaría justo la cifra que 426 usa para
 * decidir si la captura por cámara es la que produce archivos malos.
 */
export function toMethod(captureMethod?: CaptureMethod): TelemetryMethod {
  return captureMethod === 'camera' ? 'getUserMedia' : 'input_file';
}

export function toNetwork(network?: string): TelemetryNetwork | undefined {
  if (!network) {
    return undefined;
  }
  return (TELEMETRY_NETWORKS as readonly string[]).includes(network)
    ? (network as TelemetryNetwork)
    : 'unknown';
}

/** Una extensión fuera de la lista se omite: el servidor descartaría el evento entero por ella. */
export function toExt(ext?: string): TelemetryExt | undefined {
  if (!ext) {
    return undefined;
  }
  const lower = ext.toLowerCase();
  return (TELEMETRY_EXTS as readonly string[]).includes(lower)
    ? (lower as TelemetryExt)
    : undefined;
}

/**
 * El servidor acota la fracción a `[0,1]` y descarta el evento entero si se pasa. Se recorta acá en
 * vez de confiar: el ratio sale de una división y un `1.0000000000000002` por error de punto
 * flotante costaría el evento completo, que es un precio absurdo por el último dígito.
 */
export function toRatio(value?: number): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return Math.min(1, Math.max(0, value));
}

/** Los campos de duración y tamaño son enteros en el esquema; un decimal invalida el evento. */
function toWholeNumber(value?: number): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return undefined;
  }
  return Math.round(value);
}

/** Descarta las claves sin valor para que el evento no viaje lleno de `undefined`. */
function defined<T extends object>(source: T): T {
  const out = {} as T;
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined && value !== null) {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

/** ISO-8601 en UTC, que es el único formato que el servidor acepta en `occurred_at`. */
export function isoUtc(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

/**
 * Arma el evento del cable. Devuelve `null` cuando no hay `inspection_id`: el servidor lo exige como
 * entero positivo, así que un intento sin sesión no tiene evento que mandar. Antes se emitía igual
 * porque Sentry lo aceptaba; ahora sería un evento que el servidor descarta en silencio.
 */
export function buildTelemetryEvent(ctx: UploadAttemptContext): TelemetryEvent | null {
  if (!Number.isInteger(ctx.inspectionId) || (ctx.inspectionId as number) <= 0) {
    return null;
  }
  if (!ctx.attemptId) {
    return null;
  }
  return defined<TelemetryEvent>({
    attempt_id: ctx.attemptId,
    inspection_id: ctx.inspectionId as number,
    result: toResult(ctx.outcome, ctx.rejectionCause),
    capture_method: toMethod(ctx.captureMethod),
    uuid: ctx.fileUuid,
    key: ctx.keyPath,
    // Viaja SIEMPRE y no solo cuando es formulario: si se omitiera en el camino de inspección,
    // el evento sería indistinguible del de una app vieja y la consulta de 426 tendría un tercer
    // estado que no significa nada.
    origin: ctx.origin ?? 'inspeccion',
    rejection_cause: toCause(ctx.rejectionCause),
    ext: toExt(ctx.ext),
    size_metadata_bytes: toWholeNumber(ctx.declaredSizeBytes),
    size_real_bytes: toWholeNumber(ctx.realSizeBytes),
    uploaded_size_bytes: toWholeNumber(ctx.uploadedSizeBytes),
    dark_pixel_ratio: toRatio(ctx.darkPixelRatio),
    prepare_ms: toWholeNumber(ctx.prepareMs),
    upload_ms: toWholeNumber(ctx.uploadMs),
    total_ms: toWholeNumber(ctx.totalMs),
    network: toNetwork(ctx.network),
    occurred_at: ctx.occurredAt,
  });
}

/** Parte la cola en lotes que el servidor acepta; uno más grande lo rechaza entero. */
export function toBatches(
  events: TelemetryEvent[],
  maxPerBatch: number = MAX_EVENTS_PER_BATCH,
): TelemetryEvent[][] {
  const size = Math.max(1, Math.floor(maxPerBatch));
  const batches: TelemetryEvent[][] = [];
  for (let i = 0; i < events.length; i += size) {
    batches.push(events.slice(i, i + size));
  }
  return batches;
}
